// src/layout/builders/ProjectInvite/ProjectInviteForm.jsx
import React, { useState } from "react";
import useProjectEnqStore from "../../../store/builders/useProjectEnqStore";
import SuccessPopup from "../../../components/common/SuccessPopup";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup"; // Import ConfirmationPopup
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom"; // Import useNavigate

const ProjectInviteForm = () => {
  const { sendProjectInvite } = useProjectEnqStore();
  const navigate = useNavigate(); // Initialize navigate
  const [customerDucktailId, setCustomerDucktailId] = useState("");
  const [projectData, setProjectData] = useState({
    projectName: "",
    projectLocation: "",
    projectBudget: "",
    startingDate: "",
    deadlineDate: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false); // Local state for API call loading

  // --- Success Popup State ---
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  // --- End Success Popup State ---

  // --- Confirmation Popup State ---
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [confirmPopupMessage, setConfirmPopupMessage] = useState("");
  // --- End Confirmation Popup State ---

  const handleChange = (e) => {
    const { id, value } = e.target;
    let processedValue = value;

    if (id === "projectBudget") {
      // Allow only numbers and a single comma for thousands separators (optional for input)
      // This is more for user input convenience; parsing will handle the final number.
      processedValue = value.replace(/[^0-9,]/g, "");
    } else if (id === "projectName" || id === "projectLocation") {
      // Basic text, allow spaces, letters, numbers, common punctuation
      // You might want to refine this regex based on allowed characters
      // processedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, ''); // Example: more restrictive
    }

    setProjectData({ ...projectData, [id]: processedValue });
  };

  const handleCustomerIdChange = (e) => {
    // Allow DT, numbers, hyphen, and space for flexibility during typing
    // Validation will enforce the final pattern
    const value = e.target.value.toUpperCase();
    setCustomerDucktailId(value);
  };

  const clearForm = () => {
    setCustomerDucktailId("");
    setProjectData({
      projectName: "",
      projectLocation: "",
      projectBudget: "",
      startingDate: "",
      deadlineDate: "",
    });
  };

  const validateForm = () => {
    // Ducktail ID validation
    const ducktailIdPattern = /^DT[- ]?\d{6}$/i; // Allows DT-123456, DT123456, DT 123456 (case-insensitive for DT)
    if (!customerDucktailId.trim()) {
      toast.error("Customer Ducktail ID is required.");
      return false;
    }
    if (!ducktailIdPattern.test(customerDucktailId.trim())) {
      toast.error(
        "Invalid Ducktail ID format. Expected: DT-XXXXXX, DTXXXXXX, or DT XXXXXX."
      );
      return false;
    }

    // Project Name
    if (!projectData.projectName.trim()) {
      toast.error("Project Name is required.");
      return false;
    }

    // Project Location
    if (!projectData.projectLocation.trim()) {
      toast.error("Project Location is required.");
      return false;
    }

    // Project Budget
    if (!projectData.projectBudget.trim()) {
      toast.error("Project Budget is required.");
      return false;
    }
    const budgetValue = parseFloat(projectData.projectBudget.replace(/,/g, ""));
    if (isNaN(budgetValue) || budgetValue <= 0) {
      toast.error("Project Budget must be a valid positive number.");
      return false;
    }

    // Starting Date (already handled by required, but ensure it's not empty string if not using HTML5 required)
    if (!projectData.startingDate) {
      toast.error("Starting Date is required.");
      return false;
    }

    // Deadline Date
    if (!projectData.deadlineDate) {
      toast.error("Deadline Date is required.");
      return false;
    }
    if (
      projectData.deadlineDate &&
      projectData.startingDate &&
      new Date(projectData.deadlineDate) < new Date(projectData.startingDate)
    ) {
      toast.error("Deadline must be on or after the project starting date.");
      return false;
    }

    return true; // All validations passed
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (!validateForm()) {
      return; // Stop if validation fails
    }

    // --- Show Confirmation Popup ---
    setConfirmPopupMessage(
      `Are you sure you want to send a project invitation to Customer ID: ${customerDucktailId.trim()}?`
    );
    setIsConfirmPopupOpen(true);
  };

  const handleConfirmInvite = async () => {
    setIsConfirmPopupOpen(false);
    setIsSubmitting(true);

    // Prepare data for the store function (parse budget)
    const finalProjectData = {
      ...projectData,
      projectBudget: projectData.projectBudget.replace(/,/g, ""), // Send cleaned budget
    };

    try {
      const result = await sendProjectInvite(
        customerDucktailId.trim(), // Send trimmed ID
        finalProjectData
      );

      if (result.success) {
        setSuccessPopupMessage(result.message);
        setIsSuccessPopupOpen(true);
      }
    } catch (error) {
      console.error("Error sending project invite:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessPopupAndClear = () => {
    setIsSuccessPopupOpen(false);
    clearForm();
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="flex items-center justify-center p-3">
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={closeSuccessPopupAndClear}
        message={successPopupMessage}
      />
      <ConfirmationPopup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        onConfirm={handleConfirmInvite}
        message={confirmPopupMessage}
        title="Confirm Invitation"
        confirmText="Send Invite"
        isLoading={isSubmitting}
      />
      <div className="w-full max-w-xl bg-white rounded-lg shadow-md p-5">
        <h2 className="text-lg font-semibold text-gray-700 mb-3 text-center">
          Project Invitation
        </h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label
              htmlFor="customerId"
              className="block text-2xs font-medium text-gray-600 mb-1"
            >
              Ducktail ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="customerId"
              value={customerDucktailId}
              onChange={handleCustomerIdChange}
              placeholder="Enter Customer's Ducktail ID (e.g., DT-123456)"
              disabled={isSubmitting || isConfirmPopupOpen}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="projectName"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="projectName"
                value={projectData.projectName}
                onChange={handleChange}
                placeholder="Enter Project Name"
                disabled={isSubmitting || isConfirmPopupOpen}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="projectLocation"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="projectLocation"
                value={projectData.projectLocation}
                onChange={handleChange}
                placeholder="Enter Village Location"
                disabled={isSubmitting || isConfirmPopupOpen}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="projectBudget"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Project Budget (INR) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="projectBudget"
                value={projectData.projectBudget}
                onChange={handleChange}
                title="Please enter numbers and commas only (e.g., 10,00,000)"
                placeholder="Estimated Budget (e.g., 500,000)"
                disabled={isSubmitting || isConfirmPopupOpen}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="startingDate"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Starting Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="startingDate"
                value={projectData.startingDate}
                onChange={handleChange}
                min={today}
                disabled={isSubmitting || isConfirmPopupOpen}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="deadlineDate"
                className="block text-2xs font-medium text-gray-600 mb-1"
              >
                Deadline <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="deadlineDate"
                value={projectData.deadlineDate}
                onChange={handleChange}
                min={projectData.startingDate || today}
                disabled={isSubmitting || isConfirmPopupOpen}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-between items-center pt-2">
            <button
              type="button"
              onClick={clearForm}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-xs transition-colors"
              disabled={isSubmitting || isConfirmPopupOpen}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs flex items-center justify-center transition-colors ${
                isSubmitting || isConfirmPopupOpen
                  ? "opacity-70 cursor-not-allowed"
                  : ""
              }`}
              disabled={isSubmitting || isConfirmPopupOpen}
            >
              {isSubmitting ? (
                <>
                  <AiOutlineLoading3Quarters className="animate-spin mr-1.5 h-3 w-3" />
                  Sending...
                </>
              ) : (
                "Send Invite"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectInviteForm;
